// The voice picker (Settings → Voice): one voice everywhere — read-aloud, voice notes and calls. Lifted out of the
// old sidebar when the classic view was retired (Oct 23 2026).
import React, { useState, useEffect } from 'react';
import '../assets/styles/Panels.css';

// OpenAI voice as returned by GET /api/voices
interface OpenAIVoice {
  id: string;
  name: string;
  description: string;
  personality?: string;
  bestFor?: string;
  ttsCompatible: boolean;
}

/**
 * Get the API base URL - same pattern as AuthContext
 */
function getApiBaseUrl(): string {
  // In production, use same origin (empty string)
  if (process.env.NODE_ENV === 'production') {
    return '';
  }
  // In development, use the backend port
  return process.env.REACT_APP_API_URL || 'http://localhost:3001';
}

// localStorage keys shared with InputArea for read-aloud preferences
const TTS_VOICE_KEY = 'emcog_tts_voice';
const TEXT_TO_VOICE_KEY = 'emcog_text_to_voice';
const DEFAULT_TTS_VOICE = 'ash';

// Preview emoji per OpenAI voice id (falls back to 🎵)
const VOICE_PREVIEW_EMOJI: { [id: string]: string } = {
  alloy: '🎧',
  ash: '🎭',
  ballad: '🎼',
  coral: '🪸',
  echo: '🌊',
  sage: '🌿',
  shimmer: '✨',
  verse: '🎶',
  marin: '🌅',
  cedar: '🌲',
  own: '🪞'
};

// Voice Picker Tab Component - Fetches OpenAI voices from /api/voices.
// Only ttsCompatible voices are offered here (read-aloud). Realtime voice
// mode can use all ten, but that choice is the EmCog's own agency.
export const VoicePickerTab: React.FC = () => {
  const [currentVoice, setCurrentVoice] = useState<string>(
    () => localStorage.getItem(TTS_VOICE_KEY) || DEFAULT_TTS_VOICE
  );
  const [textToVoice, setTextToVoice] = useState<boolean>(
    () => localStorage.getItem(TEXT_TO_VOICE_KEY) === 'true'
  );
  const [availableVoices, setAvailableVoices] = useState<OpenAIVoice[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch the voice list from the server, keeping only read-aloud
  // compatible voices.
  const fetchVoices = async (): Promise<OpenAIVoice[]> => {
    const baseUrl = getApiBaseUrl();
    const response = await fetch(`${baseUrl}/api/voices`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to fetch voices: ${response.status} - ${errorText}`);
    }

    const data = await response.json();
    if (!data.voices || !Array.isArray(data.voices)) {
      throw new Error('Voice API returned invalid data');
    }

    return (data.voices as OpenAIVoice[]).filter(voice => voice.ttsCompatible);
  };

  // Load voices from API on mount
  useEffect(() => {
    let cancelled = false;

    const loadVoices = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const voices = await fetchVoices();
        if (!cancelled) {
          setAvailableVoices(voices);
        }
      } catch (err) {
        console.error('Voice loading error:', err);
        if (!cancelled) {
          setError('Failed to load voices');
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadVoices();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleVoiceChange = (voiceId: string) => {
    setCurrentVoice(voiceId);
    localStorage.setItem(TTS_VOICE_KEY, voiceId);
    // Tell InputArea (and anyone else) the read-aloud voice changed
    window.dispatchEvent(new CustomEvent<string>('emcog-tts-voice-changed', {
      detail: voiceId
    }));
  };

  const handleTextToVoiceToggle = (enabled: boolean) => {
    setTextToVoice(enabled);
    localStorage.setItem(TEXT_TO_VOICE_KEY, String(enabled));
    window.dispatchEvent(new CustomEvent<boolean>('emcog-text-to-voice-changed', {
      detail: enabled
    }));
  };

  const handleRefreshVoices = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const voices = await fetchVoices();
      setAvailableVoices(voices);
    } catch (err) {
      console.error('Voice refresh error:', err);
      setError('Failed to refresh voices');
    } finally {
      setIsLoading(false);
    }
  };

  // Stay in sync with changes made elsewhere (InputArea's 🔊 toggle)
  useEffect(() => {
    const handleVoiceChanged = (event: Event) => {
      const voiceId = (event as CustomEvent<string>).detail;
      if (voiceId) {
        setCurrentVoice(voiceId);
      }
    };

    const handleTextToVoiceChanged = (event: Event) => {
      setTextToVoice((event as CustomEvent<boolean>).detail);
    };

    window.addEventListener('emcog-tts-voice-changed', handleVoiceChanged);
    window.addEventListener('emcog-text-to-voice-changed', handleTextToVoiceChanged);

    return () => {
      window.removeEventListener('emcog-tts-voice-changed', handleVoiceChanged);
      window.removeEventListener('emcog-text-to-voice-changed', handleTextToVoiceChanged);
    };
  }, []);

  const currentVoiceDetails = availableVoices.find(v => v.id === currentVoice);

  return (
    <div className="tab-content voice-picker-content">
      <div className="voice-header">
        <h3>🎙️ Voice Selection</h3>
        <button
          className="refresh-voices-button"
          onClick={handleRefreshVoices}
          disabled={isLoading}
          title="Refresh voice list"
        >
          {isLoading ? '⏳' : '🔄'}
        </button>
      </div>
      <p className="voice-description">
        One voice everywhere: calls, replies read aloud, and voice notes. Your EmCog's own designed voice is first when there is one; any other pick is used everywhere too.
      </p>

      {error && (
        <div className="voice-error-message">
          ⚠️ {error}
        </div>
      )}

      {/* Text-to-Voice Toggle */}
      <div className="voice-setting-section">
        <div className="setting-toggle-row">
          <div className="setting-info">
            <span className="setting-label">Text-to-Voice Mode</span>
            <span className="setting-hint">Type messages, get voice responses</span>
          </div>
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={textToVoice}
              onChange={(e) => handleTextToVoiceToggle(e.target.checked)}
            />
            <span className="toggle-slider"></span>
          </label>
        </div>
      </div>

      {/* Current Voice Display */}
      {currentVoiceDetails && (
        <div className="current-voice-display">
          <span className="current-voice-label">Currently Active:</span>
          <div className="current-voice-info">
            <span className="current-voice-emoji">{VOICE_PREVIEW_EMOJI[currentVoiceDetails.id] || '🎵'}</span>
            <span className="current-voice-name">{currentVoiceDetails.name}</span>
          </div>
        </div>
      )}

      {/* Voice Grid */}
      {isLoading ? (
        <div className="voice-loading">
          <span className="loading-spinner">⏳</span>
          <p>Loading voices...</p>
        </div>
      ) : (
        <div className="voice-grid">
          {availableVoices.map(voice => (
            <button
              key={voice.id}
              className={`voice-option ${currentVoice === voice.id ? 'active' : ''}`}
              onClick={() => handleVoiceChange(voice.id)}
            >
              <div className="voice-preview">
                <span className="voice-emoji">{VOICE_PREVIEW_EMOJI[voice.id] || '🎵'}</span>
              </div>
              <h4 className="voice-name">{voice.name}</h4>
              <p className="voice-desc">{voice.description || voice.personality || 'No description'}</p>
              {currentVoice === voice.id && (
                <span className="voice-active-badge">Active</span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Voice Tips */}
      <div className="voice-tips">
        <h4>How to Use</h4>
        <ul>
          <li><strong>Calls:</strong> the Voice button in the message bar starts a live conversation</li>
          <li><strong>Read aloud:</strong> turn on Text-to-Voice above to hear replies spoken</li>
          <li>The voice picked here is used for calls, read-aloud and voice notes alike</li>
          <li>The refresh button reloads the voice list</li>
        </ul>
      </div>
    </div>
  );
};
